import usePrevious from 'hooks/usePrevious';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, produce } from 'utility/general';

const useCollaboratingPartyData = ({ formId = -1, siteInfoId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const serverTableKey = 'collaborating';
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const dispatch = useDispatch();
    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const removeAllTempData = () => {
        setRows(
            rows.filter((item) => {
                return !item.id.toString().startsWith('temp_');
            }),
        );
    };

    const defaultFormList = {
        // number: {
        //     value: '',
        //     label: 'No.',
        //     name: 'number',
        //     type: 'input',
        //     disabled: true,
        //     // validators: ['isRequired'],
        // },
        name: {
            value: '',
            label: 'Name of organization / individual',
            name: 'name',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
    };

    const handleEditClick = (row) => {
        setSelectedRow(row);
        setOpenPopup(true);
    };

    const handleRowCreate = async (id) => {
        setSelectedRow(null);
        setOpenPopup(true);
    };

    const handleDelete = async (row) => {
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                siteInfoId,
                applicationFormId: formId,
            },
        });
    };

    const onRecordUpdate = async (data) => {
        data.id = `temp_${generateRandomId()}`;
        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });
    };

    const onEditSubmit = (row) => {
        let postData = updatePostData ?? [];

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );
    };

    const columns = [
        {
            field: 'id',
            headerName: 'No.',
            fixWidth: true,
            valueGetter: (params, index) => {
                // if (params.row.id.toString().startsWith('temp_')) {
                //     return '-';
                // }

                return index + 1;
            },
            width: 80,
        },
        { field: 'name', headerName: 'Name of organization / individual', width: 400 },
    ];

    return {
        columns,
        rows,
        openPopup,
        setOpenPopup,
        defaultFormList,
        selectedRow,
        handleRowCreate,
        onRecordUpdate,
        handleEditClick,
        handleDelete,
        setRows,
        onEditSubmit,
        removeAllTempData,
    };
};

export default useCollaboratingPartyData;
